import Phaser from 'phaser';
import { InputManager } from './InputManager';

/**
 * TouchControls: Virtual on-screen touch buttons for mobile devices.
 * Uses DOM overlay elements (not canvas) so buttons maintain their actual pixel size
 * regardless of canvas scaling, ensuring proper touch targets on all screen sizes.
 * Only visible on touch-capable devices.
 */
export class TouchControls {
  private scene: Phaser.Scene;
  private inputManager: InputManager;
  private isVisible = false;
  private overlayContainer: HTMLDivElement | null = null;
  private pendingTimeouts: ReturnType<typeof setTimeout>[] = [];

  constructor(scene: Phaser.Scene, inputManager: InputManager) {
    this.scene = scene;
    this.inputManager = inputManager;
  }

  /**
   * Detect if touch controls should be shown.
   * Shows on touch-capable devices. Desktop users with keyboard get no touch overlay.
   * Can be forced with URL param ?touch=1 for testing.
   */
  private static isTouchDevice(scene: Phaser.Scene): boolean {
    // URL param override for testing
    const params = new URLSearchParams(window.location.search);
    if (params.get('touch') === '1') return true;
    if (params.get('touch') === '0') return false;

    // Check Phaser's touch detection (checks 'ontouchstart' in window)
    return scene.sys.game.device.input.touch;
  }

  create(): void {
    const isTouchCapable = TouchControls.isTouchDevice(this.scene);

    if (!isTouchCapable) {
      this.isVisible = false;
      return;
    }

    this.isVisible = true;
    this.createDOMControls();
  }

  private createDOMControls(): void {
    // Create a DOM overlay container that sits on top of the game canvas
    this.overlayContainer = document.createElement('div');
    this.overlayContainer.id = 'touch-controls';
    this.overlayContainer.style.cssText = `
      position: fixed;
      top: 0;
      left: 0;
      width: 100%;
      height: 100%;
      pointer-events: none;
      z-index: 1000;
      user-select: none;
      -webkit-user-select: none;
      -webkit-touch-callout: none;
    `;

    // --- LEFT BUTTON ---
    const leftBtn = this.createButton({
      html: '◀',
      bottom: '20px',
      left: '15px',
      width: '64px',
      height: '64px',
      borderRadius: '12px',
      bgColor: 'rgba(0, 255, 255, 0.25)',
      borderColor: 'rgba(0, 255, 255, 0.4)',
      fontSize: '24px',
    });
    leftBtn.addEventListener('touchstart', (e) => {
      e.preventDefault();
      this.inputManager.setTouchState('left', true);
      leftBtn.style.background = 'rgba(0, 255, 255, 0.5)';
    }, { passive: false });
    leftBtn.addEventListener('touchend', (e) => {
      e.preventDefault();
      this.inputManager.setTouchState('left', false);
      leftBtn.style.background = 'rgba(0, 255, 255, 0.25)';
    }, { passive: false });
    leftBtn.addEventListener('touchcancel', () => {
      this.inputManager.setTouchState('left', false);
      leftBtn.style.background = 'rgba(0, 255, 255, 0.25)';
    });
    // Also handle mouse events for ?touch=1 desktop testing
    leftBtn.addEventListener('mousedown', (e) => {
      e.preventDefault();
      this.inputManager.setTouchState('left', true);
      leftBtn.style.background = 'rgba(0, 255, 255, 0.5)';
    });
    leftBtn.addEventListener('mouseup', () => {
      this.inputManager.setTouchState('left', false);
      leftBtn.style.background = 'rgba(0, 255, 255, 0.25)';
    });
    leftBtn.addEventListener('mouseleave', () => {
      this.inputManager.setTouchState('left', false);
      leftBtn.style.background = 'rgba(0, 255, 255, 0.25)';
    });
    this.overlayContainer.appendChild(leftBtn);

    // --- RIGHT BUTTON ---
    const rightBtn = this.createButton({
      html: '▶',
      bottom: '20px',
      left: '90px',
      width: '64px',
      height: '64px',
      borderRadius: '12px',
      bgColor: 'rgba(0, 255, 255, 0.25)',
      borderColor: 'rgba(0, 255, 255, 0.4)',
      fontSize: '24px',
    });
    rightBtn.addEventListener('touchstart', (e) => {
      e.preventDefault();
      this.inputManager.setTouchState('right', true);
      rightBtn.style.background = 'rgba(0, 255, 255, 0.5)';
    }, { passive: false });
    rightBtn.addEventListener('touchend', (e) => {
      e.preventDefault();
      this.inputManager.setTouchState('right', false);
      rightBtn.style.background = 'rgba(0, 255, 255, 0.25)';
    }, { passive: false });
    rightBtn.addEventListener('touchcancel', () => {
      this.inputManager.setTouchState('right', false);
      rightBtn.style.background = 'rgba(0, 255, 255, 0.25)';
    });
    rightBtn.addEventListener('mousedown', (e) => {
      e.preventDefault();
      this.inputManager.setTouchState('right', true);
      rightBtn.style.background = 'rgba(0, 255, 255, 0.5)';
    });
    rightBtn.addEventListener('mouseup', () => {
      this.inputManager.setTouchState('right', false);
      rightBtn.style.background = 'rgba(0, 255, 255, 0.25)';
    });
    rightBtn.addEventListener('mouseleave', () => {
      this.inputManager.setTouchState('right', false);
      rightBtn.style.background = 'rgba(0, 255, 255, 0.25)';
    });
    this.overlayContainer.appendChild(rightBtn);

    // --- FIRE BUTTON (large, right side) ---
    const fireBtn = this.createButton({
      html: 'FIRE',
      bottom: '15px',
      right: '15px',
      width: '80px',
      height: '80px',
      borderRadius: '50%',
      bgColor: 'rgba(255, 50, 50, 0.3)',
      borderColor: 'rgba(255, 50, 50, 0.5)',
      fontSize: '13px',
      fontFamily: '"Press Start 2P", monospace',
    });
    fireBtn.addEventListener('touchstart', (e) => {
      e.preventDefault();
      this.inputManager.setTouchState('fire', true);
      fireBtn.style.background = 'rgba(255, 50, 50, 0.6)';
    }, { passive: false });
    fireBtn.addEventListener('touchend', (e) => {
      e.preventDefault();
      this.inputManager.setTouchState('fire', false);
      fireBtn.style.background = 'rgba(255, 50, 50, 0.3)';
    }, { passive: false });
    fireBtn.addEventListener('touchcancel', () => {
      this.inputManager.setTouchState('fire', false);
      fireBtn.style.background = 'rgba(255, 50, 50, 0.3)';
    });
    fireBtn.addEventListener('mousedown', (e) => {
      e.preventDefault();
      this.inputManager.setTouchState('fire', true);
      fireBtn.style.background = 'rgba(255, 50, 50, 0.6)';
    });
    fireBtn.addEventListener('mouseup', () => {
      this.inputManager.setTouchState('fire', false);
      fireBtn.style.background = 'rgba(255, 50, 50, 0.3)';
    });
    fireBtn.addEventListener('mouseleave', () => {
      this.inputManager.setTouchState('fire', false);
      fireBtn.style.background = 'rgba(255, 50, 50, 0.3)';
    });
    this.overlayContainer.appendChild(fireBtn);

    // --- PAUSE BUTTON (small, top-right) ---
    const pauseBtn = this.createButton({
      html: '⏸',
      top: '10px',
      right: '10px',
      width: '44px',
      height: '44px',
      borderRadius: '8px',
      bgColor: 'rgba(128, 128, 128, 0.2)',
      borderColor: 'rgba(128, 128, 128, 0.35)',
      fontSize: '20px',
    });
    pauseBtn.addEventListener('touchstart', (e) => {
      e.preventDefault();
      this.inputManager.setTouchState('pause', true);
      pauseBtn.style.background = 'rgba(128, 128, 128, 0.5)';
      const tid = setTimeout(() => {
        this.inputManager.setTouchState('pause', false);
        pauseBtn.style.background = 'rgba(128, 128, 128, 0.2)';
      }, 150);
      this.pendingTimeouts.push(tid);
    }, { passive: false });
    pauseBtn.addEventListener('mousedown', (e) => {
      e.preventDefault();
      this.inputManager.setTouchState('pause', true);
      pauseBtn.style.background = 'rgba(128, 128, 128, 0.5)';
      const tid = setTimeout(() => {
        this.inputManager.setTouchState('pause', false);
        pauseBtn.style.background = 'rgba(128, 128, 128, 0.2)';
      }, 150);
      this.pendingTimeouts.push(tid);
    });
    this.overlayContainer.appendChild(pauseBtn);

    // Add overlay to the page
    document.body.appendChild(this.overlayContainer);
  }

  /**
   * Create a styled button element for touch controls
   */
  private createButton(opts: {
    html: string;
    top?: string;
    bottom?: string;
    left?: string;
    right?: string;
    width: string;
    height: string;
    borderRadius: string;
    bgColor: string;
    borderColor: string;
    fontSize: string;
    fontFamily?: string;
  }): HTMLButtonElement {
    const btn = document.createElement('button');
    btn.innerHTML = opts.html;

    let positionCSS = '';
    if (opts.top) positionCSS += `top: ${opts.top};`;
    if (opts.bottom) positionCSS += `bottom: ${opts.bottom};`;
    if (opts.left) positionCSS += `left: ${opts.left};`;
    if (opts.right) positionCSS += `right: ${opts.right};`;

    btn.style.cssText = `
      position: absolute;
      ${positionCSS}
      width: ${opts.width};
      height: ${opts.height};
      border-radius: ${opts.borderRadius};
      background: ${opts.bgColor};
      border: 2px solid ${opts.borderColor};
      color: rgba(255, 255, 255, 0.6);
      font-size: ${opts.fontSize};
      font-family: ${opts.fontFamily || 'monospace'};
      display: flex;
      align-items: center;
      justify-content: center;
      pointer-events: auto;
      touch-action: none;
      -webkit-tap-highlight-color: transparent;
      outline: none;
      cursor: pointer;
      padding: 0;
      margin: 0;
      box-shadow: 0 0 10px ${opts.borderColor};
    `;

    return btn;
  }

  /**
   * Check if touch controls are currently visible
   */
  getIsVisible(): boolean {
    return this.isVisible;
  }

  /**
   * Show or hide touch controls
   */
  setVisible(visible: boolean): void {
    if (this.overlayContainer) {
      this.overlayContainer.style.display = visible ? 'block' : 'none';
    }
    this.isVisible = visible;
  }

  /**
   * Clean up touch controls
   */
  destroy(): void {
    // Clear any pending timeouts to prevent callbacks after destruction
    for (const tid of this.pendingTimeouts) {
      clearTimeout(tid);
    }
    this.pendingTimeouts = [];

    if (this.overlayContainer) {
      this.overlayContainer.remove();
      this.overlayContainer = null;
    }
    this.isVisible = false;
  }
}
